<template>
  <div>
    <div class="flex items-center gap-4 px-4 py-3 border-b border-gray-100">
      <button type="button" :class="TEXT_BUTTON" @click="addCase">Add case</button>
      <button type="button" :class="TEXT_BUTTON" @click="showImport = !showImport">Import JSON</button>
      <button type="button" :class="TEXT_BUTTON" @click="exportJson">Export JSON</button>
      <div v-if="dirty" class="ml-auto flex items-center gap-3">
        <span class="text-gray-600">Unsaved changes</span>
        <button type="button" :class="TEXT_BUTTON" @click="reset">Discard</button>
        <button type="button" :class="PRIMARY_BUTTON" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
      </div>
    </div>
    <p v-if="saveError" class="px-4 pt-3 text-red-600">{{ saveError }}</p>

    <div v-if="showImport" class="px-4 py-4 border-b border-gray-100">
      <span class="block text-gray-500 mb-2">A JSON list of cases: {label, tenant, prompt, hats?, rubric?}. Replaces the whole list; save afterwards.</span>
      <textarea v-model="importText" :class="[INPUT, 'font-mono h-40']" />
      <div class="flex items-center gap-3 mt-2">
        <button type="button" :class="TEXT_BUTTON" @click="importJson">Replace list</button>
        <span v-if="importError" class="text-red-600">{{ importError }}</span>
      </div>
    </div>

    <p v-if="!cases.length" class="px-4 py-6 text-gray-500">No cases yet. Add one from a turn on the Console tab.</p>
    <ul v-else class="divide-y divide-gray-100">
      <li v-for="(c, i) in cases" :key="i" class="flex items-start gap-4 px-4 py-3">
        <input type="checkbox" class="mt-1" :checked="selected.includes(c.label)" :title="'Include in the next run'" @change="$emit('select', c.label)">
        <div class="min-w-0 flex-1">
          <div class="flex items-baseline gap-2">
            <span class="font-medium text-gray-900">{{ c.label }}</span>
            <span class="text-gray-500">{{ c.tenant || bench.tenantName }}</span>
          </div>
          <p class="sensitive mt-0.5 truncate text-gray-600" :title="c.prompt" data-demo-blur>{{ c.prompt || '—' }}</p>
        </div>
        <div class="flex shrink-0 items-center gap-3 pt-0.5">
          <a v-if="c.source_session" :href="`/agent/${c.source_session}`" target="_blank" rel="noopener" :class="TEXT_BUTTON">Source</a>
          <button type="button" :class="TEXT_BUTTON" @click="edit(i)">Edit</button>
          <button type="button" :class="TEXT_BUTTON" @click="remove(i)">Remove</button>
        </div>
      </li>
    </ul>

    <div v-if="editing !== null" class="fixed inset-0 z-40 bg-black/30 flex items-center justify-center" @click.self="editing = null">
      <div class="bg-white rounded-lg border border-gray-200 w-[56rem] max-w-[95vw] max-h-[90vh] flex flex-col">
        <div class="px-5 py-3 border-b border-gray-100 flex items-center">
          <h3 class="font-semibold text-gray-900">{{ draft.label || 'New case' }}</h3>
          <button class="ml-auto text-gray-400 hover:text-gray-600" @click="editing = null">×</button>
        </div>
        <div class="p-5 overflow-y-auto grid grid-cols-12 gap-4">
          <label class="col-span-5"><span class="block text-gray-500 mb-2">Label</span><input v-model="draft.label" :class="INPUT"></label>
          <label class="col-span-2"><span class="block text-gray-500 mb-2">Tenant</span><input v-model="draft.tenant" :class="INPUT" placeholder="vytalize"></label>
          <label class="col-span-5"><span class="block text-gray-500 mb-2">Required hat path (optional, comma-separated)</span><input v-model="draft.hats" :class="INPUT" placeholder="population,practice_performance,reporter"></label>
          <label class="col-span-12">
            <span class="block text-gray-500 mb-2">Opening prompt</span>
            <textarea v-model="draft.prompt" :class="[INPUT, 'sensitive h-40']" data-demo-blur />
          </label>
          <label class="col-span-12"><span class="block text-gray-500 mb-2">Rubric: what a good outcome is</span><input v-model="draft.rubric" :class="INPUT"></label>
          <p class="col-span-12 text-gray-500">The judge plays the user: it sends the opening prompt, answers the agent, follows up until the conversation is done, then scores it against the rubric. It never approves a write.</p>
        </div>
        <div class="px-5 py-3 border-t border-gray-100 flex items-center gap-3">
          <span v-if="draftError" class="text-red-600">{{ draftError }}</span>
          <button type="button" :class="[SECONDARY_BUTTON, 'ml-auto']" @click="editing = null">Cancel</button>
          <button type="button" :class="PRIMARY_BUTTON" @click="applyDraft">Done</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH, errorOf } from '../hermes';
import { caseFrom, casesOf, saveCases } from './bench';


function toDraft(c) {
  return {
    label: c.label, hats: c.hats || '', rubric: c.rubric || '', tenant: c.tenant || '', source_session: c.source_session || '',
    prompt: c.prompt || '', source_run: c.source_run || null,
  };
}

function fromDraft(d) {
  return caseFrom({
    label: d.label.trim(), hats: d.hats.trim(), rubric: d.rubric.trim(),
    tenant: String(d.tenant || '').trim(), source_session: d.source_session || '',
    prompt: String(d.prompt || '').trim(), source_run: d.source_run || null,
  });
}

export default {
  name: 'TestCases',
  props: { bench: { type: Object, required: true }, selected: { type: Array, default: () => [] } },
  emits: ['refresh', 'select'],
  data() {
    const cases = casesOf(this.bench.template);
    return {
      cases, original: JSON.stringify(cases), saving: false, saveError: '',
      showImport: false, importText: '', importError: '', editing: null, draft: toDraft(caseFrom({})), draftError: '',
      INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    dirty() {
      return JSON.stringify(this.cases) !== this.original;
    },
  },
  methods: {
    plain(c) {
      const copy = { ...c };
      delete copy.kind;
      return copy;
    },
    addCase() {
      this.cases.push(caseFrom({ label: `Case ${this.cases.length + 1}` }));
      this.edit(this.cases.length - 1);
    },
    edit(i) {
      this.draft = toDraft(this.cases[i]);
      this.draftError = '';
      this.editing = i;
    },
    applyDraft() {
      const c = fromDraft(this.draft);
      if (!c.label) { this.draftError = 'A case needs a label.'; return; }
      if (!c.prompt) { this.draftError = 'A case needs a prompt.'; return; }
      this.cases.splice(this.editing, 1, c);
      this.editing = null;
    },
    remove(i) {
      this.cases.splice(i, 1);
    },
    reset() {
      this.cases = JSON.parse(this.original);
    },
    importJson() {
      this.importError = '';
      let list;
      try {
        list = JSON.parse(this.importText);
      } catch (e) {
        this.importError = `Not valid JSON: ${e.message}`;
        return;
      }
      if (!Array.isArray(list) || !list.length) { this.importError = 'Expected a non-empty JSON list.'; return; }
      this.cases = list.map(caseFrom);
      this.showImport = false;
      this.importText = '';
    },
    exportJson() {
      const text = JSON.stringify(this.cases.map((c) => this.plain(c)), null, 2);
      const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'agent_bench_cases.json';
      a.click();
      URL.revokeObjectURL(url);
    },
    async save() {
      this.saving = true;
      this.saveError = '';
      try {
        await saveCases(this.$axios, this.bench, this.cases);
        this.original = JSON.stringify(this.cases);
        this.$emit('refresh');
      } catch (e) {
        this.saveError = errorOf(e, 'The cases were not saved.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
